import { getSearchParamsFromRoute } from "@utils";
import {
	PERSISTENT_SEARCH_HEIGHT,
	RESULTS_CONTROLLER_HEIGHT,
	STICKY_TUCK,
} from "@/plugins/mixins/sticky-results-offset";
import { API_MAX_RESULTS, PAGES } from "@/common/consts";
import {
	calculateRangeForPagination,
	generatePaginationShownWordsText,
	generateShowMoreShownWordsText,
} from "~/pages/Results/helpers";

export default {
	/**
	 * The actions column is always appended, so we only
	 * need to look for 2 fields to know that there are 3 columns.
	 **/
	hasThreeColumns() {
		return this.fields.length === 2;
	},
	isLengthPage() {
		return (
			this.$route.name === PAGES.WORDS_BY_LENGTH_RESULTS ||
			this.$route.name ===
				PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS
		);
	},
	/**
	 * The Wordle endpoint returns no highlight data — every letter comes back
	 * as regular in `_mod` — so on those pages the mods are synthesised from
	 * the search params instead. Null anywhere the API's own mods should win.
	 */
	wordleHighlightParams() {
		if (
			![PAGES.WORDLE_RESULTS, PAGES.QUORDLE_RESULTS].includes(
				this.$route.name
			)
		) {
			return null;
		}

		const params = getSearchParamsFromRoute(this.$route);

		return {
			correct: params.clean?.wordle?.correct || "",
			includes: params.clean?.wordle?.includes || "",
		};
	},
	currentPageContent() {
		const content = [...this.tableWords, ...this.tableWordsExtended].map(
			(word, index) => {
				word.highlightedWord = this.highlightWord(word.word, word.mod);
				return {
					word,
					// Only the pill layout renders per-letter segments; the
					// table layout uses `highlightedWord` above.
					segments: this.usePillsLayout
						? this.getWordHighlightSegments(word)
						: null,
					ad: null,
					isVisible: index < this.itemsToShow,
				};
			}
		);

		this.rowAds.forEach((ad) => {
			if (content[ad.position - 1]) {
				content[ad.position - 1].ad = {
					stub: ad.stub,
					type: ad.type,
					showAd: ad.showAd,
					placement: ad.placement || null,
					positionHTL: ad.positionHTL || null,
				};
			}
		});

		return content;
	},
	tableWords() {
		return this.words || [];
	},
	tableTotal() {
		return this.wordsMeta?.total || API_MAX_RESULTS;
	},
	wordCountLabel() {
		if (
			[
				PAGES.WORDS_BY_LENGTH_RESULTS,
				PAGES.WORDLE_RESULTS,
				PAGES.QUORDLE_RESULTS,
				PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS,
			].includes(this.$route.name)
		) {
			return null;
		}

		const noun = this.tableTotal === 1 ? "word" : "words";
		return `${this.tableTotal} ${noun}`;
	},
	paginationString() {
		if (this.buttonMode === null) {
			return "";
		}
		const { start, end } = calculateRangeForPagination(
			this.$route.query.page || 1,
			this.itemsToShow,
			this.tableTotal
		);
		const variables = {
			ShownWords: this.isLengthPage
				? generatePaginationShownWordsText(start, end, this.tableTotal)
				: generateShowMoreShownWordsText(
						this.itemsToShow,
						this.tableTotal
				  ),
			TotalWords: this.tableTotal,
		};
		const dictKey = this.isSmallDevice
			? "ResultTableTextWordsShownMobile"
			: "ResultTableTextWordsShown";
		return this.$t(dictKey, variables);
	},
	/**
	 * Where the table's own sticky header parks: below everything sticky above
	 * it. On small devices that's the persistent search bar and the controller;
	 * on larger ones the bar is hidden, leaving just the controller.
	 *
	 * The numbers are the same totals this used to reach with a bare `75` and a
	 * store value that was never actually written to — decomposed here so each
	 * term says what it is. WFW-6748 left a follow-up to give the whole sticky
	 * stack one ordered declaration; until then these heights are still stated
	 * separately from the CSS that applies them.
	 */
	headerOffsetTop() {
		if (!this.tableRef) {
			return null;
		}
		const clearanceHeight =
			this.$store.getters["window/headerClearanceHeight"];

		if (!this.isSmallDevice) {
			return `${clearanceHeight + RESULTS_CONTROLLER_HEIGHT.large}px`;
		}

		return `${
			clearanceHeight +
			PERSISTENT_SEARCH_HEIGHT +
			RESULTS_CONTROLLER_HEIGHT.small -
			STICKY_TUCK * 2
		}px`;
	},
};
